import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import AdminLayout from '@/components/feature/AdminLayout';

interface ManufacturerDoc {
  id: string;
  manufacturer_name: string;
  document_type: 'letter_of_authorization' | 'quality_certificate' | 'catalogue';
  file_name: string;
  file_path: string;
  file_size: number;
  created_at: string;
}

type DocType = 'letter_of_authorization' | 'quality_certificate' | 'catalogue';

const DOC_LABELS: Record<DocType, { label: string; icon: string }> = {
  letter_of_authorization: { label: 'Letter of Authorization', icon: 'ri-file-text-line' },
  quality_certificate: { label: 'Quality Certificate', icon: 'ri-award-line' },
  catalogue: { label: 'Catalogue', icon: 'ri-book-open-line' },
};

const ALL_DOC_TYPES: DocType[] = ['letter_of_authorization', 'quality_certificate', 'catalogue'];

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function AdminManufacturerDocsPage() {
  const [docs, setDocs] = useState<ManufacturerDoc[]>([]);
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedMfr, setExpandedMfr] = useState<string | null>(null);
  const [addingMfr, setAddingMfr] = useState(false);
  const [newMfrName, setNewMfrName] = useState('');
  const [uploadingFor, setUploadingFor] = useState<{ manufacturer: string; docType: DocType } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUpload, setPendingUpload] = useState<{ manufacturer: string; docType: DocType; file: File } | null>(null);

  const fetchDocs = useCallback(async () => {
    try {
      const { data, error: fetchErr } = await supabase
        .from('manufacturer_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;

      const docsData = (data || []) as ManufacturerDoc[];
      setDocs(docsData);
      const uniqueMfrs = [...new Set(docsData.map((d) => d.manufacturer_name))];
      setManufacturers(uniqueMfrs);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const getDocForManufacturer = (mfr: string, docType: DocType): ManufacturerDoc | undefined => {
    return docs.find((d) => d.manufacturer_name === mfr && d.document_type === docType);
  };

  const handleAddManufacturer = async () => {
    const trimmed = newMfrName.trim();
    if (!trimmed) return;
    if (manufacturers.includes(trimmed)) {
      setError('Manufacturer already exists.');
      return;
    }
    setManufacturers((prev) => [...prev, trimmed]);
    setNewMfrName('');
    setAddingMfr(false);
    setError('');
    setExpandedMfr(trimmed);
  };

  const handleFileSelect = (manufacturer: string, docType: DocType) => {
    setUploadingFor({ manufacturer, docType });
    setError('');
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 50);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFor) return;

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File is too large. Maximum size is 50MB.');
      setUploadingFor(null);
      e.target.value = '';
      return;
    }

    setPendingUpload({ ...uploadingFor, file });
    setUploadingFor(null);
    e.target.value = '';
  };

  useEffect(() => {
    if (!pendingUpload) return;

    const doUpload = async () => {
      const { manufacturer, docType, file } = pendingUpload;
      setError('');

      const ext = file.name.split('.').pop() || 'bin';
      const safeMfr = manufacturer.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
      const storagePath = `${safeMfr}/${docType}_${Date.now()}.${ext}`;

      try {
        const { error: uploadErr } = await supabase.storage
          .from('manufacturer-docs')
          .upload(storagePath, file, { upsert: true, cacheControl: '3600' });

        if (uploadErr) throw uploadErr;

        const existing = getDocForManufacturer(manufacturer, docType);
        if (existing) {
          const { error: delErr } = await supabase
            .from('manufacturer_documents')
            .delete()
            .eq('id', existing.id);

          if (delErr) console.error('Failed to delete old record:', delErr);

          try {
            await supabase.storage.from('manufacturer-docs').remove([existing.file_path]);
          } catch { /* ignore cleanup errors */ }
        }

        const { error: insertErr } = await supabase
          .from('manufacturer_documents')
          .insert({
            manufacturer_name: manufacturer,
            document_type: docType,
            file_name: file.name,
            file_path: storagePath,
            file_size: file.size,
          });

        if (insertErr) throw insertErr;

        await fetchDocs();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        console.error('Upload error:', err);
        setError(message);
      } finally {
        setPendingUpload(null);
      }
    };

    doUpload();
  }, [pendingUpload]);

  const handleDelete = async (doc: ManufacturerDoc) => {
    setDeletingId(doc.id);
    setError('');
    try {
      const { error: storageErr } = await supabase.storage
        .from('manufacturer-docs')
        .remove([doc.file_path]);

      if (storageErr) console.error('Storage delete error:', storageErr);

      const { error: dbErr } = await supabase
        .from('manufacturer_documents')
        .delete()
        .eq('id', doc.id);

      if (dbErr) throw dbErr;

      await fetchDocs();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteManufacturer = async (mfr: string) => {
    setError('');
    const mfrDocs = docs.filter((d) => d.manufacturer_name === mfr);

    try {
      if (mfrDocs.length > 0) {
        const paths = mfrDocs.map((d) => d.file_path);
        const { error: storageErr } = await supabase.storage
          .from('manufacturer-docs')
          .remove(paths);

        if (storageErr) console.error('Storage cleanup error:', storageErr);

        const { error: dbErr } = await supabase
          .from('manufacturer_documents')
          .delete()
          .eq('manufacturer_name', mfr);

        if (dbErr) throw dbErr;
      }

      setManufacturers((prev) => prev.filter((m) => m !== mfr));
      if (expandedMfr === mfr) setExpandedMfr(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      setError(message);
    }
  };

  const handleDownload = async (doc: ManufacturerDoc) => {
    try {
      const { data } = await supabase.storage
        .from('manufacturer-docs')
        .createSignedUrl(doc.file_path, 60);

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to generate download link.');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900">Manufacturer Documents</h1>
            <p className="text-sm text-foreground-500 mt-1">Securely store Letters of Authorization, Quality Certificates, and Catalogues per manufacturer.</p>
          </div>
          <button
            onClick={() => { setAddingMfr(true); setError(''); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            Add Manufacturer
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto flex-shrink-0 hover:text-red-900 cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {/* Add manufacturer inline form */}
        {addingMfr && (
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 flex items-center gap-3 flex-wrap">
            <i className="ri-building-line text-lg text-foreground-400" />
            <input
              type="text"
              value={newMfrName}
              onChange={(e) => setNewMfrName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddManufacturer(); }}
              placeholder="Enter manufacturer name..."
              className="flex-1 min-w-[200px] px-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
              autoFocus
            />
            <button
              onClick={handleAddManufacturer}
              disabled={!newMfrName.trim()}
              className="px-4 py-2.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-lg hover:bg-accent-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              Save
            </button>
            <button
              onClick={() => { setAddingMfr(false); setNewMfrName(''); }}
              className="px-4 py-2.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : manufacturers.length === 0 ? (
          <div className="bg-background-50 border border-background-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-400">
              <i className="ri-folder-open-line text-2xl" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-1">No Manufacturers Yet</h3>
            <p className="text-sm text-foreground-500 mb-4">Add your first manufacturer to start uploading documents.</p>
            <button
              onClick={() => { setAddingMfr(true); setError(''); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line" />
              Add Manufacturer
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {manufacturers.map((mfr) => {
              const isExpanded = expandedMfr === mfr;
              return (
                <div key={mfr} className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                  {/* Manufacturer header */}
                  <button
                    onClick={() => setExpandedMfr(isExpanded ? null : mfr)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center text-accent-600 flex-shrink-0">
                        <i className="ri-building-line" />
                      </div>
                      <span className="font-heading font-semibold text-foreground-900 text-sm">{mfr}</span>
                      <span className="text-xs text-foreground-400 bg-foreground-100 px-2 py-0.5 rounded-full">
                        {docs.filter((d) => d.manufacturer_name === mfr).length} / 3 docs
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteManufacturer(mfr); }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title="Delete manufacturer and all documents"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                      {isExpanded ? (
                        <i className="ri-arrow-up-s-line text-foreground-400 transition-transform duration-200" />
                      ) : (
                        <i className="ri-arrow-down-s-line text-foreground-400 transition-transform duration-200" />
                      )}
                    </div>
                  </button>

                  {/* Expanded document rows */}
                  {isExpanded && (
                    <div className="border-t border-background-200 divide-y divide-background-100">
                      {ALL_DOC_TYPES.map((docType) => {
                        const doc = getDocForManufacturer(mfr, docType);
                        const isUploading = pendingUpload?.manufacturer === mfr && pendingUpload?.docType === docType;
                        const isDeleting = doc && deletingId === doc.id;

                        return (
                          <div key={docType} className="flex items-center justify-between px-5 py-3.5 gap-4 flex-wrap">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center text-secondary-600 flex-shrink-0">
                                <i className={`${DOC_LABELS[docType].icon} text-sm`} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground-800">{DOC_LABELS[docType].label}</p>
                                {doc && !isUploading ? (
                                  <p className="text-xs text-foreground-500 truncate">
                                    {doc.file_name} · {formatSize(doc.file_size)}
                                  </p>
                                ) : isUploading ? (
                                  <p className="text-xs text-accent-600 flex items-center gap-1">
                                    <span className="w-3 h-3 border-2 border-accent-500 border-t-transparent rounded-full animate-spin inline-block" />
                                    Uploading...
                                  </p>
                                ) : (
                                  <p className="text-xs text-foreground-400">No file uploaded</p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {doc && !isUploading && (
                                <>
                                  <button
                                    onClick={() => handleDownload(doc)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                                    title="Download"
                                  >
                                    <i className="ri-download-line" />
                                  </button>
                                  <button
                                    onClick={() => handleFileSelect(mfr, docType)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-500 hover:bg-accent-50 transition-colors cursor-pointer"
                                    title="Replace file"
                                  >
                                    <i className="ri-refresh-line" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(doc)}
                                    disabled={isDeleting}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                                    title="Delete file"
                                  >
                                    {isDeleting ? (
                                      <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin inline-block" />
                                    ) : (
                                      <i className="ri-close-line" />
                                    )}
                                  </button>
                                </>
                              )}
                              {!doc && !isUploading && (
                                <button
                                  onClick={() => handleFileSelect(mfr, docType)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-600 bg-accent-50 hover:bg-accent-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-upload-line" />
                                  Upload
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
          onChange={handleFileChange}
        />
      </div>
    </AdminLayout>
  );
}